import type { InternalRequest } from '../client.js';
import type { Device, DevicePhoto } from '@trackizy/types';

type Params = Record<string, string | number | boolean | undefined | null>;

function qsFrom(params?: Params): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, String(v));
  return `?${usp}`;
}

export function devicesEndpoints(req: InternalRequest) {
  return {
    list: (params?: Params) => req.request<Device[]>(`/devices${qsFrom(params)}`),
    get: (id: number | string) => req.request<Device>(`/devices/${id}`),
    create: (data: Partial<Device>) =>
      req.request<Device>('/devices', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number | string, data: Partial<Device>) =>
      req.request<Device>(`/devices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number | string) =>
      req.request<void>(`/devices/${id}`, { method: 'DELETE' }),
    accumulators: (id: number | string, data: Record<string, unknown>) =>
      req.request<unknown>(`/devices/${id}/accumulators`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    /** Per-device custom-field value PATCH — null clears a key. */
    updateCustomFields: (deviceId: number | string, values: Record<string, unknown>) =>
      req.request<Device>(`/devices/${deviceId}/custom-fields`, {
        method: 'PATCH',
        body: JSON.stringify(values),
      }),
    /** Device photos (up to 5 per device; stored via /api/storage). */
    photos: {
      list: (deviceId: number | string) =>
        req.request<DevicePhoto[]>(`/devices/${deviceId}/photos`),
      create: (deviceId: number | string, data: Record<string, unknown>) =>
        req.request<unknown>(`/devices/${deviceId}/photos`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      delete: (deviceId: number | string, photoId: number | string) =>
        req.request<void>(`/devices/${deviceId}/photos/${photoId}`, { method: 'DELETE' }),
    },
  };
}
